import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BottomTabBar from "./components/BottomTabBar";
import ScrollToTop from "./components/ScrollToTop";
import { ExitIntentOffer } from "./components/ExitIntentOffer";
import PageViewTracker from "./components/PageViewTracker";
import usePushNotifications from "./hooks/usePushNotifications";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import KundliReportPage from "./pages/KundliReportPage";
import KundliFormPage from "./pages/KundliFormPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ChatPage from "./pages/ChatPage";
import HoroscopePage from "./pages/HoroscopePage";
import MatchingPage from "./pages/MatchingPage";
import DashboardPage from "./pages/DashboardPage";
import AdminDashboard from "./pages/AdminDashboard";
import ConsultationPage from "./pages/ConsultationPage";
import LiveChatPage from "./pages/LiveChatPage";
import BlogPage from "./pages/BlogPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AccountPrivacyPage from "./pages/AccountPrivacyPage";
import RefundsPage from "./pages/RefundsPage";
import MobileConnectPage from "./pages/MobileConnectPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import PayPage from "./pages/PayPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import { SEO_SLUGS } from "./lib/seoPages";
import "./App.css";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#120021" }}>
      <div className="animate-spin text-4xl">✦</div>
    </div>
  );
  if (!user) return <Navigate to="/auth?tab=login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  usePushNotifications(user); // Register FCM token when user is logged in (no-op on web)
  // Emergent Google auth return — must be detected synchronously during render
  // (read from useLocation().hash, NOT window.location.hash, so React re-renders after replaceState)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <PageViewTracker />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/mobile/connect" element={<MobileConnectPage />} />
        <Route path="/pay" element={<PayPage />} />
        <Route path="/account/delete" element={<AccountDeletionPage />} />
        <Route path="/kundli" element={<KundliFormPage />} />
        <Route path="/kundli/report" element={<KundliReportPage />} />
        <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset" element={<ResetPasswordPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/horoscope" element={<HoroscopePage />} />
        <Route path="/matching" element={<MatchingPage />} />
        <Route path="/consultation" element={<ConsultationPage />} />
        <Route path="/livechat/:chatId" element={
          <ProtectedRoute><LiveChatPage /></ProtectedRoute>
        } />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/account/privacy" element={<AccountPrivacyPage />} />
        <Route path="/refunds" element={<RefundsPage />} />
        {SEO_SLUGS.map(slug => (
          <Route key={slug} path={`/${slug}`} element={<SeoLandingPage slug={slug} />} />
        ))}
        <Route path="/dashboard" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <div className={location.pathname === "/chat" ? "footer-hide-mobile" : undefined}>
        <Footer />
      </div>
      <BottomTabBar />
      <ExitIntentOffer />
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <CurrencyProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </I18nProvider>
  );
}
